import { c as _c } from "react/compiler-runtime";
import { IcoInfo16 } from '@onefootprint/icons';
import type { DataIdentifier, DocumentKind, VaultKind } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner, Tooltip } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import useCurrentEntity from '@/entities/components/details/hooks/use-current-entity';
import useEntityId from '@/entities/components/details/hooks/use-entity-id';
import type { VaultType } from '@/entities/hooks/use-entity-vault';
import useAutoDecryptControls from '@/entity/hooks/use-auto-decrypt-controls';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import type { EntityVault } from '@/types/vault';
import getRelevantNonDocumentKeys from '../../../utils/get-relevant-non-document-keys';
type DecryptProps = {
  canDecrypt: boolean;
  onDecryptSuccess: (vault: VaultType) => void;
  documentType: DocumentKind;
  vaultData: VaultType | undefined;
  seqno: string | undefined;
  entityKind: VaultKind;
};
const Decrypt = t0 => {
  const $ = _c(23);
  const {
    canDecrypt,
    onDecryptSuccess,
    documentType,
    vaultData,
    seqno,
    entityKind
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset.documents.details.identity.decrypt"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const entityId = useEntityId();
  const {
    data: entity
  } = useCurrentEntity();
  const decryptControls = useDecryptControls();
  const autoDecryptControls = useAutoDecryptControls(entityKind);
  let t2;
  if ($[1] !== decryptControls || $[2] !== documentType || $[3] !== entity || $[4] !== onDecryptSuccess || $[5] !== seqno || $[6] !== vaultData?.vault) {
    t2 = async () => {
      if (!entity) {
        return;
      }
      const relevantKeys = getRelevantNonDocumentKeys(documentType);
      const fields = Object.keys(vaultData?.vault || {}).filter(key => relevantKeys.includes(key as DataIdentifier)).map(key_0 => `${key_0}${seqno ? `:${seqno}` : ""}` as DataIdentifier);
      const response = await decryptControls.decrypt({
        entity,
        fields,
        reason: `Examining document ${documentType}`
      });
      const vault = Object.entries(response ?? {}).reduce((acc, t3) => {
        const [key_1, value] = t3;
        const di = seqno ? key_1.split(":")[0] : key_1;
        acc[di] = value;
        return acc;
      }, {} as EntityVault);
      onDecryptSuccess({
        vault,
        transforms: {}
      });
    };
    $[1] = decryptControls;
    $[2] = documentType;
    $[3] = entity;
    $[4] = onDecryptSuccess;
    $[5] = seqno;
    $[6] = vaultData?.vault;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleDecryptSubmit = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IcoInfo16 color="tertiary" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t(_temp);
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <div className="flex gap-2 items-center">{t3}<p className="text-body-2 text-tertiary">{t4}</p></div>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== autoDecryptControls || $[14] !== canDecrypt || $[15] !== decryptControls || $[16] !== entityId || $[17] !== handleDecryptSubmit || $[18] !== t) {
    t6 = autoDecryptControls.isEntityAutoDecrypting(entityId) || decryptControls.isDecrypting ? <LoadingSpinner /> : <Tooltip disabled={canDecrypt} content={t(_temp2)}><LinkButton disabled={!canDecrypt} onClick={handleDecryptSubmit} className="underline">{t(_temp3)}</LinkButton></Tooltip>;
    $[13] = autoDecryptControls;
    $[14] = canDecrypt;
    $[15] = decryptControls;
    $[16] = entityId;
    $[17] = handleDecryptSubmit;
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <div className="flex justify-between items-center">{t5}{t6}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};
export default Decrypt;
function _temp($) {
  return $.text;
}
function _temp2($_0) {
  return $_0.notAllowed;
}
function _temp3($_1) {
  return $_1.decryptAll;
}